import { Match, Portal, render, Show, Switch } from 'solid-js/web';
import { AppProvider, useApp, UserContext } from './context';
import * as api from './api';
import { Accessor, createEffect, createMemo, createResource, createSignal, For, JSX, Setter, Suspense } from 'solid-js';
import { createOptions, Select } from '@thisbeyond/solid-select';
import { createAsync } from '@solidjs/router';
import "@thisbeyond/solid-select/style.css";
import { AddrField } from './bindings/AddrField';
import { currencies, CurrencyDef, format_price } from './util';
import { createStore } from 'solid-js/store';
import { Input } from './common';


type LoginForm = {
    email: string;
    password: string;
};
type AddrFields = { [key in AddrField]?: string };

type OrderForm = {
  name: string,
  addr: api.AddrData,
};

const AddrFieldLabels = {
  ContactName: "Contact Name",
  HouseNumber: "House Number",
};
function addrFieldLabel(field: AddrField): string {
  return AddrFieldLabels[field] ?? field;
}


export function OrderForm() {
  const { cart, user } = useApp();
  const countryList = createAsync(() => api.country_list());
  const [name, setName] = createSignal<string>("");
  const [country, setCountry] = createSignal<{code: string, name: string}>(undefined);
  const [currency, setCurrency] = createSignal<api.Currency>("EUR");
  const [addr, setAddr] = createStore<AddrFields>({});

  const fields = createAsync(async () => {
    const c = country();
    if (c !== undefined) {
      return await api.addr_fields(c.code);
    }
  });
  createEffect(() => console.log("fields:", fields()));

  const [orderPreviewArgs, setOrderPreviewArgs] = createSignal<api.OrderReq>();
  const [orderArgs, setOrderArgs] = createSignal<api.OrderReq>();

  const [preview] = createResource<api.OrderPreview, api.OrderReq>(orderPreviewArgs, (req) => api.order_preview(req));
  const [order] = createResource<api.Order, api.OrderReq>(orderArgs, (req) => api.order(req));

  createEffect(() => {
    if (order() !== undefined) {
      cart.clear();
    }
  })

  const handleSubmit = () => {
    setOrderPreviewArgs({
      addr: { country_code: country().code, data: { Fields: addr } },
      cart: cart.items,
      currency: currency(),
      name: name()
    });
  };
  const handleOrder = () => {
    if (orderArgs()) { return }
    setOrderArgs({
      addr: { country_code: country().code, data: { Fields: addr } },
      cart: cart.items,
      currency: currency(),
      name: name()
    });
  };
  const submitDisabled = () => (name().length == 0 || country() === undefined || user.user() === null);

  return (
    <Switch>
      <Match when={order() === undefined}>
      <div>
        <div class="form">
          <div class="input">
            <label>Currency</label>
            <Select options={currencies} format={(code: CurrencyDef) => code.label} onChange={(c: CurrencyDef) => setCurrency(c.id)} initialValue={currencies[0]} />
          </div>
          <div class="input">
            <label>Country</label>
            <Show when={countryList()} keyed fallback={<div>loading...</div>}>
              {(opt) => {
                const list = opt.list.map(([code, name]) => { return { code, name }})
                const props = createOptions(list, {
                  format: ({name}) => name ?? "",
                  filterable: true,
                  extractText: ({name}) => name,
                });
                const initial = list.find(({code}) => code == opt.ip.country);
                return <Select {...props} onChange={(val) => setCountry(val)} initialValue={initial} />
              }}
            </Show>
          </div>
          <Input label="Name" type="text" onchange={(e) => setName(e.target.value)} />
        </div>
        <Show when={fields()} keyed>
          {(fields) => <div class="frame">
            <div class="head">
              <label>Shipping address</label>
            </div>
            <div class="form">
              <For each={fields}>
                {(field) => {
                  switch (field.type) {
                    case "newline": return <br />;
                    case "separator": return <span innerText={field.sep} />;
                    case "field":
                      if (field.field == "Country") {
                        return <Input 
                          label={addrFieldLabel(field.field)}
                          type="text"
                          disabled={true}
                          value={country().name} />;
                      }
                      return <Input
                        label={addrFieldLabel(field.field)}
                        type="text"
                        onchange={(e) => setAddr(field.field, e.target.value)}
                        value={addr[field.field]}
                        />;
                  }
                }}
              </For>
            </div>
          </div>
        }
        </Show>

        <button disabled={submitDisabled()} onclick={handleSubmit}>Check</button>

        <Show when={preview()} keyed>
          {preview => <OrderPreview resp={preview} />}
        </Show>
        <button onclick={handleOrder} disabled={preview() === undefined}>Place Order</button>
        </div>
      </Match>
      <Match when={order() !== undefined}>
        <h2>Order recieved</h2>
        <p>Your Order Nr {order().order_id} has been recieved.</p>
        <p>When we have confirmed that everything is correct, you will recieve an E-Mail with the payment information.</p>
      </Match>
    </Switch>);
}

const OrderPreview = ({resp}: {resp: api.OrderPreview}) => (<table>
    <thead>
      <tr>
        <th>Item Nr.</th>
        <th>Description</th>
        <th>Quantity</th>
        <th>Unit price</th>
        <th>Price</th>
      </tr>
    </thead>
    <tbody>
      <For each={resp.items}>
        {(item) => <tr>
          <td>{item.id}</td>
          <td>{item.desc}</td>
          <td>{item.quantity}</td>
          <td>{item.unit_price}</td>
          <td>{item.value}</td>
        </tr>}
      </For>
    </tbody>
    <tfoot>
      <tr>
        <th colspan={4}>Subtotal</th>
        <th>{format_price(resp.total)}</th>
      </tr>
      <Show when={resp.total !== null}>
      <tr>
        <th colspan={4}>Total</th>
        <th>{format_price(resp.total)}</th>
      </tr>
      </Show>
    </tfoot>
  </table>);
